import { trackClickEvent } from '@frontend/tracking';
import { Translate } from '@frontend/translation';
import { Typography, useTheme } from '@mui/material';
import { useIntl } from 'react-intl';
import {
  FakeSeparator,
  ImageWrapper,
  BackgroundWrapper,
  HeadingWrapper,
  LayoutWrapper,
  LeftWrapper,
  RightWrapper,
  CTAWrapper,
} from '../GuestPageLayout/GuestPageLayout.css';
import PlatformHeader from '@customer-platform-shared/components/PlatformHeader/PlatformHeader';
import SellerDashboardImage from '@customer-platform-assets/images/dashboard.png';
import { HEADER_VARIANTS } from '@customer-platform-shared/components/PlatformHeader/PlatformHeader.types';
import { Button, InnerTypography } from '@frontend/ui-elements';
import { PLATFORM_ROUTES } from '@frontend/routing';
import { IUserLoginRequest, useLoginMutation } from '@frontend/api';
import { SubmitHandler } from 'react-hook-form';
import FormType from '@customer-platform-shared/components/Forms/utils/FormType.types';
import { useEffect, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import InfoModal from '@customer-platform-shared/components/Modals/InfoModal/InfoModal';
import useModal from '@customer-platform-shared/components/Modals/useModal/useModal';
import SuccessModal from '@customer-platform-shared/components/Modals/SuccessModal/SuccessModal';
import LoginLinkExpiredModal from '@customer-platform-shared/components/Modals/LoginLinkExpiredModal/LoginLinkExpiredModal';
import { RE_LOGIN_SOURCE } from '../LoginValidation/LoginValidation.const';
import LoginForm from './LoginForm/LoginForm';

export function Login() {
  const intl = useIntl();
  const theme = useTheme();
  const location = useLocation();
  const sourceUrl = location.pathname;
  const params = useMemo(
    () => new URLSearchParams(location.search),
    [location.search],
  );
  const redirectUrl = params.get('redirectUrl') ?? undefined;
  const role = params.get('user') ?? undefined;
  const { mutate, isSuccess, isPending, isError, error } = useLoginMutation({
    redirectUrl,
    sourceUrl,
  });

  const {
    closeModal,
    openInfoModal,
    isInfoModalOpen,
    openSuccessModal,
    isSuccessModalOpen,
    openLinkExpiredModal,
    isLinkExpiredModalOpen,
  } = useModal();

  useEffect(() => {
    const queryParamsSource = new URLSearchParams(location.search).get(
      'source',
    );
    if (queryParamsSource === RE_LOGIN_SOURCE) {
      openLinkExpiredModal();
    }
  }, [location, openLinkExpiredModal]);

  useEffect(() => {
    if (isSuccess && openSuccessModal) {
      openSuccessModal();
    }
  }, [isSuccess, openSuccessModal]);

  useEffect(() => {
    const responseErrors = error?.response?.data;
    if (!responseErrors && isError) {
      openInfoModal();
    }
  }, [error, isError, openInfoModal]);

  const onSubmit: SubmitHandler<FormType<IUserLoginRequest>> = loginData => {
    mutate(loginData);
  };

  const OnboardingImage = () => {
    const imageId = 'sell-metal.visual-onboarding.image';
    const imageAltText = intl.formatMessage({
      id: 'customer-platform.signup.visual-onboarding.image-alt',
    });

    return (
      <ImageWrapper
        id={imageId}
        onClick={() =>
          trackClickEvent({
            elementId: imageId,
            elementText: imageAltText,
          })
        }
      >
        <img src={SellerDashboardImage} alt={imageAltText} loading="lazy" />
      </ImageWrapper>
    );
  };
  return (
    <BackgroundWrapper>
      <PlatformHeader variant={HEADER_VARIANTS.GUEST} />
      <LayoutWrapper>
        <LeftWrapper>
          <HeadingWrapper>
            <Typography component="h2" variant="h2">
              <InnerTypography
                message={intl.formatMessage({
                  id: 'customer-platform.shared.login.heading',
                })}
                variant="h2"
                color={theme.palette.primary[500]}
              />
            </Typography>
            <CTAWrapper>
              <Typography component="p" variant="p3">
                <InnerTypography
                  message={intl.formatMessage({
                    id: 'customer-platform.shared.login.subheading',
                  })}
                  variant="p3"
                  color={theme.palette.primary[500]}
                />
              </Typography>
              <Button
                buttonType="linked"
                size="large"
                id="headerCTA"
                href={
                  role
                    ? `${PLATFORM_ROUTES.signUp}?user=${encodeURIComponent(role as string)}`
                    : PLATFORM_ROUTES.signUp
                }
              >
                <Translate id="customer-platform.shared.login.header.cta" />
              </Button>
            </CTAWrapper>
          </HeadingWrapper>

          {isSuccessModalOpen && (
            <SuccessModal
              title={intl.formatMessage({
                id: 'customer-platform.shared.login.login-form.email-sent.heading',
              })}
              text={intl.formatMessage({
                id: 'customer-platform.shared.login.login-form.email-sent.subheading',
              })}
              onClose={closeModal}
            />
          )}

          {isInfoModalOpen && (
            <InfoModal
              title={intl.formatMessage({
                id: 'customer-platform.shared.generic-error',
              })}
              primaryCta={{
                text: intl.formatMessage({
                  id: 'customer-platform.shared.close',
                }),
                onClick: () => {
                  closeModal();
                },
              }}
              severity="warning"
            />
          )}
          {isLinkExpiredModalOpen && (
            <LoginLinkExpiredModal
              onClose={closeModal}
              onClickOpenLoginModal={closeModal}
            />
          )}
          <LoginForm
            onSubmitHandler={onSubmit}
            isSuccess={isSuccess}
            isPending={isPending}
            loginErrors={error?.response?.data}
          />
        </LeftWrapper>
        <FakeSeparator />
        <RightWrapper>
          <OnboardingImage />
          <HeadingWrapper>
            <Typography
              component="h3"
              variant="h2"
              color={theme.palette.primary[500]}
            >
              <Translate id="customer-platform.signup.visual-onboarding.title" />
            </Typography>
            <Typography variant="p1" color={theme.palette.primary[500]}>
              <Translate id="customer-platform.signup.visual-onboarding.paragraph" />
            </Typography>
          </HeadingWrapper>
        </RightWrapper>
      </LayoutWrapper>
    </BackgroundWrapper>
  );
}

export default Login;
